import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import {
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";

import { getFiscalDocuments } from "../../api/DeliveryCenter/deliveryCenterCosts";

const ACCENT = "#059669";
const ACCENT_LIGHT = "#10b981";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "processing", label: "Processando" },
  { value: "completed", label: "Concluido" },
  { value: "failed", label: "Falhou" },
  { value: "pending_ai", label: "Aguardando IA" },
  { value: "ai_processed", label: "Processado pela IA" },
];

const STATUS_COLOR = {
  processing: { bg: "#fef3c7", fg: "#92400e" },
  completed: { bg: "#dcfce7", fg: "#166534" },
  failed: { bg: "#fee2e2", fg: "#991b1b" },
  pending_ai: { bg: "#dbeafe", fg: "#1e40af" },
  ai_processed: { bg: "#e0e7ff", fg: "#3730a3" },
};

const EMPTY_FILTERS = {
  source_phone_number: "",
  processing_status: "",
  processed_at_after: null,
  processed_at_before: null,
};

const formatPhone = (raw) => {
  if (!raw) return "—";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 13) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return raw;
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm:ss") : iso;
};

const FiscalDocumentsList = () => {
  const theme = useTheme();

  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const queryParams = useMemo(() => {
    const p = { page: page + 1, page_size: pageSize };
    if (appliedFilters.source_phone_number) {
      p.search = appliedFilters.source_phone_number.trim();
    }
    if (appliedFilters.processing_status) {
      p.processing_status = appliedFilters.processing_status;
    }
    if (appliedFilters.processed_at_after) {
      p.processed_at_after = dayjs(appliedFilters.processed_at_after)
        .startOf("day")
        .toISOString();
    }
    if (appliedFilters.processed_at_before) {
      p.processed_at_before = dayjs(appliedFilters.processed_at_before)
        .endOf("day")
        .toISOString();
    }
    return p;
  }, [appliedFilters, page, pageSize]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getFiscalDocuments(queryParams);
      if (Array.isArray(data)) {
        setRows(data);
        setCount(data.length);
      } else {
        setRows(data?.results || []);
        setCount(data?.count || 0);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Falha ao carregar notas recebidas",
      );
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setPage(0);
    setAppliedFilters(draftFilters);
  };

  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const renderStatusChip = (status) => {
    const color = STATUS_COLOR[status] || { bg: "#f3f4f6", fg: "#374151" };
    const option = STATUS_OPTIONS.find((o) => o.value === status);
    return (
      <Chip
        size="small"
        label={option?.label || status || "—"}
        sx={{
          backgroundColor: color.bg,
          color: color.fg,
          fontWeight: 600,
          fontSize: "0.7rem",
          height: 22,
        }}
      />
    );
  };

  const renderThumbnail = (url) => {
    if (!url) {
      return (
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: alpha(theme.palette.grey[300], 0.4),
            color: theme.palette.text.disabled,
          }}
        >
          <ImageIcon fontSize="small" />
        </Box>
      );
    }
    return (
      <Link href={url} target="_blank" rel="noopener noreferrer" underline="none">
        <Box
          component="img"
          src={url}
          alt="Nota"
          loading="lazy"
          sx={{
            width: 56,
            height: 56,
            objectFit: "cover",
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            cursor: "pointer",
            transition: "transform 0.15s ease",
            "&:hover": { transform: "scale(1.04)" },
          }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </Link>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(ACCENT, 0.02)} 0%, ${alpha(ACCENT_LIGHT, 0.05)} 100%)`,
          border: `1px solid ${alpha(ACCENT, 0.1)}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Notas recebidas
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              Auditoria das imagens enviadas pelo WhatsApp antes do
              processamento de OCR
            </Typography>
          </Box>
          <Tooltip title="Recarregar">
            <IconButton onClick={fetchData} sx={{ color: ACCENT }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Telefone / ID"
              placeholder="ex: 5511..."
              value={draftFilters.source_phone_number}
              onChange={(e) =>
                handleFilterChange("source_phone_number", e.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              size="small"
              label="Status"
              value={draftFilters.processing_status}
              onChange={(e) =>
                handleFilterChange("processing_status", e.target.value)
              }
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Recebida a partir de"
              value={draftFilters.processed_at_after}
              onChange={(d) => handleFilterChange("processed_at_after", d)}
              slotProps={{
                textField: { fullWidth: true, size: "small" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Recebida ate"
              value={draftFilters.processed_at_before}
              onChange={(d) => handleFilterChange("processed_at_before", d)}
              slotProps={{
                textField: { fullWidth: true, size: "small" },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={resetFilters}
                startIcon={<CloseIcon />}
                sx={{
                  borderColor: alpha(ACCENT, 0.3),
                  color: ACCENT,
                  "&:hover": {
                    borderColor: ACCENT,
                    backgroundColor: alpha(ACCENT, 0.04),
                  },
                }}
              >
                Limpar
              </Button>
              <Button
                variant="contained"
                onClick={applyFilters}
                startIcon={<FilterListIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_LIGHT} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(ACCENT, 0.3)}`,
                  "&:hover": {
                    boxShadow: `0 6px 20px ${alpha(ACCENT, 0.4)}`,
                  },
                }}
              >
                Aplicar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            background: alpha(theme.palette.error.main, 0.06),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            color: theme.palette.error.dark,
            fontSize: "0.85rem",
          }}
        >
          {error}
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: alpha(ACCENT, 0.04) }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                  Imagem
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                  Recebida em
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                  Telefone
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                  Documento
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                  Status
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                  align="right"
                >
                  Abrir
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} sx={{ color: ACCENT }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{
                      py: 6,
                      color: theme.palette.text.disabled,
                      fontSize: "0.85rem",
                    }}
                  >
                    Nenhuma nota recebida no periodo selecionado
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      "&:hover": {
                        background: alpha(ACCENT, 0.03),
                      },
                    }}
                  >
                    <TableCell>{renderThumbnail(row.document_url)}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                      {formatDateTime(row.processed_at)}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {formatPhone(row.source_phone_number)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.7rem",
                        fontFamily: "monospace",
                        color: theme.palette.text.secondary,
                        maxWidth: 240,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Tooltip title={row.document_id || ""}>
                        <span>{row.document_id || "—"}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{renderStatusChip(row.processing_status)}</TableCell>
                    <TableCell align="right">
                      {row.document_url ? (
                        <IconButton
                          size="small"
                          component="a"
                          href={row.document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: ACCENT }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Box
                          component="span"
                          sx={{
                            color: theme.palette.text.disabled,
                            fontSize: "0.75rem",
                          }}
                        >
                          —
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Por pagina"
          labelDisplayedRows={({ from, to, count: total }) =>
            `${from}–${to} de ${total !== -1 ? total : `mais de ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
};

export default FiscalDocumentsList;
